/**
 * Handlungsliste — was ist JETZT zu tun?
 *
 * DER ABLAUF, FÜR DEN DAS GEBAUT IST — korrigiert am 12.08.2026:
 * REVOLUT IST FÜHREND. Der Nutzer handelt dort von Hand und trägt hier nach,
 * was er wirklich bezahlt hat (`UebernahmeDialog`). Er braucht deshalb genau
 * eine Sache — den Satz, was er bei Revolut eingeben soll, mit den konkreten
 * Preisen.
 *
 * Hier stand bis heute das GEGENTEIL („Der Nutzer führt im Paper-Konto dieser
 * App und bildet dieselben Trades bei Revolut nach"). Zwei Dateien
 * beschrieben denselben Ablauf in entgegengesetzter Richtung; der Dialog hat
 * recht, dieser Kopf hatte unrecht (Audit 12.08., Befund 2).
 *
 * WARUM ALS ZUSTAND UND NICHT ALS BENACHRICHTIGUNG: Mail und Push sind
 * flüchtig. Wer drei Tage nicht hineinschaut, hat sie überscrollt — und der
 * gestaffelte Ausstieg erzeugt seit dem 08.08. eine ZWEITE Handlung Wochen
 * nach dem Kauf („Hälfte verkaufen"). Diese Liste wird bei jedem Aufruf aus
 * den Kursen neu abgeleitet. Verpassen ist damit nicht möglich; eine fällige
 * Handlung bleibt stehen, bis sie erledigt ist.
 *
 * WAS SIE NICHT TUT: raten. Fehlt eine Angabe, entsteht keine Handlung. Eine
 * Kaufaufforderung ohne Stop wäre schlimmer als gar keine.
 */

import { findeStopVerletzung, bewerteStopVerletzung } from './stop-wache';
import { REST_RUECKSETZER, TEILVERKAUF_R } from './zone-signals';
import type { OhlcvBar } from '@/lib/market-data/types';

export type HandlungsArt = 'ausgestoppt' | 'teilverkauf' | 'kaufen' | 'stop_nachziehen';

export interface Handlung {
  art: HandlungsArt;
  symbol: string;
  /** Der Satz, der genau sagt, was zu tun ist. */
  satz: string;
  /** Begründung in einem Satz — warum jetzt. */
  grund: string;
  /** Konkrete Preise für die Order, soweit bestimmbar. */
  limit: number | null;
  stop: number | null;
  /** Seit wann die Handlung fällig ist. */
  faelligSeit: string | null;
  /** Wie viele Handelstage sie schon offen ist. */
  tageOffen: number;
  dringend: boolean;
  /** Nur bei `kaufen` gesetzt — für die Übernahme in eine echte Position. */
  ausloeser?: string | null;
  /** Nur bei `kaufen` gesetzt — id aus v5_zone_signals. */
  zoneSignalId?: number | null;
  /**
   * Nur bei `kaufen`: Steht der Kurs noch unter dem Limit? Ist er darüber
   * gelaufen, bleibt das Signal gültig, aber die Order wäre nicht ausführbar
   * — dann darf die Oberfläche keinen Kauf-Knopf anbieten.
   */
  kaufbar?: boolean;
}

export interface OffenePosition {
  symbol: string;
  einstieg: number;
  /** AKTUELLER Stop — er trägt die Information, ob schon teilverkauft wurde. */
  stop: number;
  /**
   * Stop beim Einstieg. Wird gebraucht, weil er das URSPRÜNGLICHE Risiko
   * definiert: Sobald der Stop auf den Einstand gezogen ist, ist das aktuelle
   * Risiko null und das +2R-Ziel wäre aus ihm nicht mehr rekonstruierbar.
   */
  initialStop: number;
  einstiegAm: string;
}

/**
 * Woran erkannt wird, dass die erste Hälfte schon draußen ist.
 *
 * Nicht an einem Häkchen, das jemand setzen muss, sondern am Stop selbst:
 * Die Handlung lautet „Hälfte verkaufen UND den Stop auf den Einstand
 * setzen". Steht der Stop dort, ist sie erledigt — und die Aufforderung
 * verschwindet von allein.
 *
 * Der Vorteil gegenüber einem Extra-Feld: Es gibt keinen Zustand, der
 * auseinanderlaufen kann. Ein Häkchen ohne nachgezogenen Stop wäre eine
 * Position, die als abgesichert gilt und es nicht ist — genau die Sorte
 * Buchhaltungslücke, die heute den Phantom-Gewinner erzeugt hat.
 *
 * Die kleine Toleranz fängt Rundung ab (Stops werden auf zwei Stellen
 * gerundet gespeichert).
 */
function teilverkaufErledigt(einstieg: number, stop: number): boolean {
  return stop >= einstieg * 0.999;
}

export interface OffeneZone {
  symbol: string;
  ausloeser: string;
  limit: number;
  stop: number;
  seit: string;
  /**
   * id aus v5_zone_signals. Optional, damit bestehende Aufrufer und Tests
   * unverändert bleiben — gebraucht wird sie nur, um einen übernommenen Kauf
   * dem auslösenden Signal zuzuordnen (src/components/dashboard/UebernahmeDialog.tsx).
   */
  zoneSignalId?: number | null;
}

/** Rangfolge — was zuerst auf dem Bildschirm steht. */
const RANG: Record<HandlungsArt, number> = {
  ausgestoppt: 0,
  teilverkauf: 1,
  kaufen: 2,
  stop_nachziehen: 3,
};

function tageSeit(bars: OhlcvBar[], datum: string): number {
  const i = bars.findIndex((b) => b.timestamp >= datum);
  return i >= 0 ? bars.length - 1 - i : 0;
}

export function leiteHandlungenAb(
  positionen: OffenePosition[],
  zonen: OffeneZone[],
  barsJeSymbol: Map<string, OhlcvBar[]>
): Handlung[] {
  const out: Handlung[] = [];
  const offeneSymbole = new Set(positionen.map((p) => p.symbol.toUpperCase()));

  for (const p of positionen) {
    const bars = barsJeSymbol.get(p.symbol.toUpperCase()) ?? [];
    if (bars.length === 0) continue;
    // ACHTUNG: Nach dem Teilverkauf steht der Stop auf dem Einstand, das
    // Risiko ist dann NULL. Ein Riegel `risiko > 0` an dieser Stelle wuerde
    // genau die abgesicherten Positionen aus der Liste werfen — also die, bei
    // denen noch der Stop nachzuziehen ist. Er gilt deshalb nur dort, wo das
    // Risiko wirklich gebraucht wird: beim +2R-Ziel.
    // Das R des Trades hängt am URSPRÜNGLICHEN Stop und ändert sich nicht,
    // wenn der Stop nachgezogen wird. Sonst wanderte das +2R-Ziel mit.
    const risiko = p.einstieg - p.initialStop;

    const seitEinstieg = bars.filter((b) => b.timestamp > p.einstiegAm);
    const laufHoch = seitEinstieg.reduce((m, b) => Math.max(m, b.close), p.einstieg);
    const schonRaus = teilverkaufErledigt(p.einstieg, p.stop);

    // Wann wurde +2R zuerst berührt? Das ist der Tag, an dem die Regel den
    // Stop auf den Einstand hebt — und damit der Tag, AB DEM der erhöhte Stop
    // überhaupt gelten kann.
    const ziel = risiko > 0 ? p.einstieg + TEILVERKAUF_R * risiko : null;
    const erreicht = ziel !== null ? seitEinstieg.find((b) => b.high >= ziel) : undefined;

    /**
     * AB WANN der aktuelle Stop gilt.
     *
     * Ein nachgezogener Stop darf NICHT gegen alte Bars geprüft werden. Sonst
     * meldet die Liste „ausgestoppt", weil der Kurs vor drei Wochen einmal
     * unter dem heutigen Stop lag — ein Fehlalarm, der die ganze Anzeige
     * unbrauchbar macht. Der angehobene Stop gilt frühestens ab dem Tag, an
     * dem +2R berührt wurde; vorher galt der ursprüngliche.
     */
    const stopGiltAb = schonRaus
      ? // Regelfall: der Stop wanderte an dem Tag hoch, an dem +2R fiel.
        erreicht?.timestamp
        // Sonderfall: Der Stop steht auf dem Einstand, aber +2R wurde nie
        // berührt — dann hat ihn jemand von Hand gesetzt und wir wissen nicht
        // wann. Rückwirkend „ausgestoppt" zu melden wäre geraten, also zählt
        // nur die jüngste Bar.
        ?? bars[bars.length - 1]!.timestamp
      : p.einstiegAm;

    // 1. Stop gerissen — das hat Vorrang vor allem anderen.
    const posT = { einstieg: p.einstieg, stop: p.stop, einstiegAm: stopGiltAb };
    const v = findeStopVerletzung(posT, bars);
    if (v) {
      const r = bewerteStopVerletzung(posT, v);
      out.push({
        art: 'ausgestoppt',
        symbol: p.symbol,
        satz: `${p.symbol} verkaufen — ${schonRaus ? 'den Rest der Position' : 'die ganze Position'}.`,
        grund:
          `Der Stop ${p.stop.toFixed(2)} wurde am ${v.datum} gerissen ` +
          `(Tief ${v.tief.toFixed(2)})` +
          (v.luecke
            ? `. Der Tag eröffnete schon bei ${v.eroeffnung.toFixed(2)}, also unter dem Stop — ` +
              'gefüllt wird dort, nicht am Stop.'
            : '.') +
          (r && !schonRaus ? ` Ergebnis ${r.r.toFixed(2)} R.` : ''),
        limit: null,
        stop: p.stop,
        faelligSeit: v.datum,
        tageOffen: v.tageUnbemerkt,
        dringend: true,
      });
      continue; // Eine ausgestoppte Position braucht keine weiteren Handlungen.
    }

    // 2. Teilverkauf bei +2R — nur wenn noch nicht erledigt.
    if (ziel !== null && erreicht && !schonRaus) {
      out.push({
        art: 'teilverkauf',
        symbol: p.symbol,
        satz: `${p.symbol}: die HÄLFTE verkaufen und den Stop auf ${p.einstieg.toFixed(2)} setzen.`,
        grund:
          `+${TEILVERKAUF_R} R sind erreicht (${ziel.toFixed(2)}, zuerst am ${erreicht.timestamp}). ` +
          `Danach den Stop des Rests auf den Einstand ${p.einstieg.toFixed(2)} setzen — ` +
          'ab dann kann der Trade nicht mehr ins Minus drehen.',
        limit: Math.round(ziel * 100) / 100,
        stop: Math.round(p.einstieg * 100) / 100,
        faelligSeit: erreicht.timestamp,
        tageOffen: tageSeit(bars, erreicht.timestamp),
        dringend: true,
      });
      continue;
    }

    // 3. Nach dem Teilverkauf: Stop nachziehen, wenn die Spur höher steht.
    if (schonRaus) {
      const spur = Math.max(p.einstieg, laufHoch * (1 - REST_RUECKSETZER));
      if (spur > p.stop * 1.001) {
        out.push({
          art: 'stop_nachziehen',
          symbol: p.symbol,
          satz: `${p.symbol}: Stop auf ${spur.toFixed(2)} nachziehen.`,
          grund:
            `Das Lauf-Hoch steht bei ${laufHoch.toFixed(2)}. Die Regel ist der höhere Wert aus ` +
            `Einstand (${p.einstieg.toFixed(2)}) und ${(REST_RUECKSETZER * 100).toFixed(0)} % darunter ` +
            `(${(laufHoch * (1 - REST_RUECKSETZER)).toFixed(2)}). Aktuell steht er bei ${p.stop.toFixed(2)}.`,
          limit: null,
          stop: Math.round(spur * 100) / 100,
          faelligSeit: stopGiltAb,
          tageOffen: tageSeit(bars, stopGiltAb),
          dringend: false,
        });
      }
    }
  }

  // 4. Offene Kaufzonen — die einzige Handlung, die Geld einsetzt.
  for (const z of zonen) {
    if (!(z.limit > z.stop)) continue; // ohne gültiges Risiko keine Order
    /**
     * KEINE Kaufanweisung für Krypto (Befund 08.08.2026, abends).
     *
     * Der Cron erzeugte an diesem Tag fünf Zonen-Signale, vier davon Krypto.
     * Alle Rückrechnungen dieses Projekts schließen Symbole mit „/"
     * ausdrücklich aus — 24/7-Handel, andere Bar-Semantik, und die
     * Zustandsmaschine ist für Aktien und ETFs gebaut. Eine Kaufanweisung auf
     * einem Instrument auszugeben, auf dem die Regel nie geprüft wurde, wäre
     * derselbe Fehler wie die widerlegten 58 %, nur in neuer Verkleidung.
     *
     * Der Filter sitzt AUCH hier und nicht nur im Cron: Vier solche Signale
     * liegen bereits in der Datenbank. Sie bleiben als Verlauf stehen, dürfen
     * aber keine Handlung auslösen.
     */
    if (z.symbol.includes('/')) continue;

    /**
     * Hältst du den Wert bereits, ist „kaufen" keine Handlung mehr (Audit
     * 10.08.2026). Das Zonensignal bleibt bewusst offen — es misst die
     * Signalgüte und darf sich davon nicht beeindrucken lassen. Aber die
     * Aufforderung, denselben Wert noch einmal zu kaufen, ist falsch: Sie
     * würde zum Nachlegen verleiten, das dieses System nirgends vorsieht,
     * und das Positionsrisiko still verdoppeln.
     */
    if (offeneSymbole.has(z.symbol.toUpperCase())) continue;

    const bars = barsJeSymbol.get(z.symbol.toUpperCase()) ?? [];

    /**
     * Ist die Zone überhaupt noch kaufbar? `resolveZoneSignalGestaffelt` hat
     * bewusst keine Zeituhr — ein Signal bleibt offen, bis es ausgestoppt
     * oder 15 % unter das Lauf-Hoch gefallen ist. Steht der Kurs inzwischen
     * ÜBER dem Limit, ist das Signal fachlich weiter gültig, aber zu diesem
     * Preis nicht mehr ausführbar. Das als „kaufen: Limit bis X" anzuzeigen
     * hiesse, eine Order zu empfehlen, die nie ausgeführt würde.
     */
    const kurs = bars.length ? bars[bars.length - 1]!.close : null;
    const nochKaufbar = kurs === null || kurs <= z.limit;

    out.push({
      art: 'kaufen',
      symbol: z.symbol,
      satz: nochKaufbar
        ? `${z.symbol} kaufen: Limit bis ${z.limit.toFixed(2)}, Stop ${z.stop.toFixed(2)}.`
        : `${z.symbol} beobachten: Kurs ${kurs.toFixed(2)} über dem Limit ${z.limit.toFixed(2)}.`,
      grund: nochKaufbar
        ? `Kaufzone offen seit ${z.seit} (Auslöser ${z.ausloeser}). Risiko je Stück `
          + `${(z.limit - z.stop).toFixed(2)} — daraus ergibt sich die Stückzahl aus deinem Risikobudget.`
        : `Die Zone ist seit ${z.seit} offen (Auslöser ${z.ausloeser}), der Kurs ist `
          + `aber über das Limit gelaufen. Zu diesem Preis würde die Order nicht `
          + `ausgeführt. Das Signal läuft weiter — kaufbar wird es erst wieder bei `
          + `einem Rücksetzer unter ${z.limit.toFixed(2)}.`,
      limit: z.limit,
      stop: z.stop,
      faelligSeit: z.seit,
      tageOffen: bars.length ? tageSeit(bars, z.seit) : 0,
      dringend: false,
      ausloeser: z.ausloeser,
      zoneSignalId: z.zoneSignalId ?? null,
      kaufbar: nochKaufbar,
    });
  }

  out.sort((a, b) => RANG[a.art] - RANG[b.art] || b.tageOffen - a.tageOffen);
  return out;
}
